import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthenticationWrapper";
import { HistoryList } from "../../../../components/HistoryList";
import CustomButton from "../../../../components/lib/customButton";
import { formatCurrency } from "../../../../utils/CurrencyUtils";
import CustomerService from "../../../../rest/customerService";
import OrderService from "../../../../rest/orderService";
import OrderInfo from "../../../../components/orderInfo";
import { formatString } from "../../../../utils/StringUtils";
import InfoField from "../../../../components/lib/infoField";
import ConfirmDeleteModal from "../../../../components/reusables/confirmDeleteModal";
import { toast } from "react-toastify";
import CreditRechargeModal from "../../../../components/reusables/creditRechargeModal";
import ExpenseModal from "../../../../components/reusables/expenseModal";
import EmployeeEditStepper from "./employeeEditStepper";
import Modal from "../../../../components/lib/modal";
import ConfirmCloseView from "../confirmCloseView";
import { useEmployeeLiveUpdate } from "../../hooks/employee/useEmployeeLiveUpdate";
import { Transition } from "@headlessui/react";

const EmployeeDetailsModalContent = ({ client, onEdit, onDelete }) => {
    const liveClient = useEmployeeLiveUpdate(client);

    const { hasPermission } = useAuth();
    const canEdit = hasPermission("customers", "update");
    const canUpdateCredits = hasPermission("customers", "update_credits");
    const canAddExpenses = hasPermission("customers", "add_expense");
    const canDelete = hasPermission("customers", "delete");

    const [orders, setOrders] = useState([]);
    const [order, setOrder] = useState(null);
    const [showOrderInfo, setShowOrderInfo] = useState(false);

    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showRechargeModal, setShowRechargeModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditSubmitting, setIsEditSubmitting] = useState(false);

    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) {
            setConfirmClose(true);
        } else {
            setShowEditModal(false);
        }
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!liveClient?.login) return;
            try {
                const [orderResult, rechargeResult, expensesResult] = await Promise.allSettled([
                    OrderService.getAllOrdersFromClient(liveClient.login),
                    OrderService.getAllCreditRechargesFromClient(liveClient.login),
                    OrderService.getAllExpensesForClient(liveClient.login),
                ]);

                const safeOrders = orderResult.status === "fulfilled" && Array.isArray(orderResult.value) ? orderResult.value : [];
                const safeRecharges = rechargeResult.status === "fulfilled" && Array.isArray(rechargeResult.value) ? rechargeResult.value : [];
                const safeExpenses = expensesResult.status === "fulfilled" && Array.isArray(expensesResult.value) ? expensesResult.value : [];

                const combinedOrders = [...safeOrders, ...safeRecharges, ...safeExpenses];
                setOrders(combinedOrders);
            } catch (error) {
                console.error(error);
            }
        }

        fetchData();
    }, [liveClient.login, liveClient.credit]);

    const handleOrderClick = (selectedOrder) => {
        setOrder(selectedOrder);
        setShowOrderInfo(true);
    };

    const handleDelete = async () => {
        try {
            await CustomerService.deleteEmployee(liveClient.login);

            toast.success("Colaborador removido com sucesso!");
            setShowModalDelete(false);
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover colaborador.");
        }
    };

    const handleEditFinish = async ({ employee }) => {
        if (isEditSubmitting) return;
        setIsEditSubmitting(true);
        try {
            await CustomerService.updateEmployee(employee);
            toast.success('Colaborador atualizado com sucesso!');
            setShowEditModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar colaborador');
        } finally {
            setIsEditSubmitting(false);
        }
    };

    const renderOrderItem = (order) => (
        <>
            <p className={`font-medium ${order.status === 'CANCELED' ? 'text-custom_red line-through' : ''}`}>
                {order.items_list
                    ? formatString(order.items_list)
                    : order.transaction_type === 'Credit Recharge Admin' ? "RECARGA CRÉDITO" : "CORREÇÃO"}
            </p>

            {order.order_id != null && (
                <p className={`font-medium ${order.status === 'CANCELED' ? 'text-custom_red line-through' : ''}`}   >
                    Total: {order.total ? formatCurrency(order.total) : formatCurrency(order.value)}
                </p>
            )}
            {order.transaction_type === 'Credit Recharge Admin' ?
                <p className={`font-medium ${order.status === 'CANCELED' ? 'text-custom_red line-through' : ''}`}   >
                    Total Creditado: {order.total ? formatCurrency(order.total) : formatCurrency(order.value)}
                </p> :
                order.transaction_type === 'Credit Expense' ?
                    <p className={`font-medium ${order.status === 'CANCELED' ? 'text-custom_red line-through' : ''}`}   >
                        Total Descontado: {order.total ? formatCurrency(order.total) : formatCurrency(order.value)}
                    </p> : <></>
            }
        </>
    );

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            <div className="space-y-3">
                                <InfoField label="Nome Completo" value={liveClient.name} />
                                <div className="grid grid-cols-1 gap-3">
                                    <InfoField label="Login (Matrícula)" value={liveClient.login} />
                                    <InfoField label="Email" value={liveClient.email || "-"} />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className={`border p-4 rounded-xl 
                                flex flex-col justify-between ${liveClient.credit >= 0 ? "bg-green-50 border-green-100 " : "bg-red-50 border-red-100 "}`}>
                                        <span className={`text-xs font-bold uppercase ${liveClient.credit >= 0 ? "text-green-700" : "text-red-500"}`}>
                                            Saldo Atual
                                        </span>
                                        <span className={`text-2xl font-bold ${liveClient.credit >= 0 ? "text-green-600" : "text-red-500"}`}>
                                            {formatCurrency(liveClient.credit)}
                                        </span>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex flex-col justify-between">
                                        <span className="text-xs text-blue-700 font-bold uppercase">Emergência Disp.</span>
                                        <span className="text-xl font-bold text-blue-600">
                                            {liveClient.credit <= 0
                                                ? formatCurrency(liveClient.emergency_value + liveClient.credit)
                                                : formatCurrency(liveClient.emergency_value)}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    <div>
                                        <p className="text-[10px] uppercase text-gray-500 font-bold">Limite Emergência</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(liveClient.emergency_value || 0)}</p>
                                    </div>
                                    <div className="border-l border-gray-300">
                                        <p className="text-[10px] uppercase text-gray-500 font-bold">Limite Diário</p>
                                        <p className="font-semibold text-gray-700">{formatCurrency(liveClient.spend_limit || 0)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            <div className="flex-1 min-h-[180px] flex flex-col border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                                    <h3 className="font-semibold text-gray-700">Histórico</h3>
                                    <span className="text-xs text-gray-400">{orders.length} registros</span>
                                </div>
                                <div className="flex-1 overflow-y-auto bg-white p-2">
                                    <HistoryList
                                        data={orders}
                                        renderItem={renderOrderItem}
                                        onItemClick={handleOrderClick}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {order && (
                        <OrderInfo
                            order={order}
                            showOrderInfoModal={showOrderInfo}
                            handleCancelClick={() => setShowOrderInfo(false)}
                            customer={liveClient}
                        />
                    )}
                </div>

                <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg">
                    <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3">
                        <CustomButton
                            buttonType="editCredits"
                            text="Recarregar"
                            onClick={() => setShowRechargeModal(true)}
                            disabled={!canUpdateCredits}
                            className="w-full sm:w-auto text-sm"
                        />
                        <CustomButton
                            buttonType="cancel"
                            text="Corrigir"
                            onClick={() => setShowExpenseModal(true)}
                            disabled={!canAddExpenses}
                            className="w-full sm:w-auto text-sm"
                        />
                        <div className="hidden sm:block w-px bg-gray-300 mx-2"></div>
                        <CustomButton
                            buttonType="edit"
                            text="Editar"
                            onClick={() => setShowEditModal(true)}
                            disabled={!canEdit}
                            className="w-full sm:w-auto text-sm"
                        />
                        <CustomButton
                            buttonType="delete"
                            text="Excluir"
                            onClick={() => setShowModalDelete(true)}
                            disabled={!canDelete}
                            className="w-full sm:w-auto text-sm"
                        />
                    </div>
                </div>

                <CreditRechargeModal
                    isOpen={showRechargeModal}
                    onClose={() => setShowRechargeModal(false)}
                    client={liveClient}
                />

                <ExpenseModal
                    isOpen={showExpenseModal}
                    onClose={() => setShowExpenseModal(false)}
                    client={liveClient}
                />

                {showEditModal && (
                    <Modal
                        isOpen={showEditModal}
                        setIsOpen={requestClose}
                        title="Editar Colaborador"
                        hideFooter
                        maxWidth="max-w-xl lg:max-w-2xl md:-mr-16"
                    >
                        <div className="relative h-full flex flex-col">
                            <EmployeeEditStepper
                                employee={liveClient}
                                isSubmitting={isEditSubmitting}
                                onCancel={requestClose}
                                onDirty={() => setHasData(true)}
                                onFinish={handleEditFinish}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView
                                        onCancel={() => setConfirmClose(false)}
                                        onConfirm={forceClose}
                                    />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir colaborador"
                    message={`Tem certeza que deseja excluir ${liveClient.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default EmployeeDetailsModalContent;